import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import Page from '../components/global/page';

import FooterLogo from '../images/svgs/footerLogo.svg';

import Grades1 from '../images/grades1.png';
import Grades2 from '../images/grades_eemb.png';
import Grades3 from '../images/grades2023.png';
import Grades4 from '../images/grades2024.png';

const LogoSpin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
`;

const LogoGlow = keyframes`
  0% {
    fill: #bbd387ff;
    stroke: #bbd387ff;
  }
  50% {
    fill: rgb(159, 209, 50);
    stroke: rgb(159, 209, 50);
  }
  100% {
    fill: #bbd387ff;
    stroke: #bbd387ff;
  }
`;

const CreditContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Credits = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const CreditList = styled.ul`
  margin: 0;
  list-style-type: none;
  padding: 0;
`;

const animationTime = 0.7;

const Logo = styled(FooterLogo)`
  height: 200px;
  width: 200px;

  #spinny {
    animation: ${({ spin }) => (spin ? LogoSpin : '')} ${animationTime}s
      ease-in-out;
    transform-origin: 50% 50%;
  }

  #leftmain,
  #leftminor,
  #centerleft,
  #centerright,
  #center,
  #rightmain,
  #rightminor {
    animation: ${LogoGlow} 10s ease-in-out infinite;
  }
`;
// Look at the recruitment page rowan setup:
// https://dailynexus.com/interactives/recruitment/
// for any inspiration

const ArticleContainer = styled.div`
  padding-bottom: 25px;
`;

const ArticleLink = styled.a`
  text-decoration: none;
  color: black;
`;

const ArticleImgCont = styled.div``;

const ArticleImg = styled.img`
  width: 250px;
  margin-right: 5px;
`;

const ArticleSep = styled.div`
  display: flex;

  @media (max-width: 550px) {
    & {
      flex-wrap: wrap-reverse;
    }
  }
`;

const ArticleWords = styled.div`
  h2,
  p {
    margin: 0;
  }
`;

const ArticleCap = styled.p`
  color: rgb(116, 116, 116);
  font-size: 12.8px;
`;

const OverviewList = styled.ul`
  * {
    margin: 10px 0;
    font-size: 18.6667px;
  }
`;

const Article = ({
  href, title, children, desc, img,
}) => (
  <ArticleContainer>
    <ArticleLink href={href}>
      <ArticleSep>
        <ArticleImg src={img} />
        <ArticleWords>
          <h2>{title}</h2>
          <ArticleCap>{children}</ArticleCap>
          <p>{desc}</p>
        </ArticleWords>
      </ArticleSep>
    </ArticleLink>
  </ArticleContainer>
);

const Index = () => {
  if (typeof window === 'undefined') {
    return <></>;
  }

const data = useStaticQuery(graphql`
  query {
    allCourseGradesCsv(sort: {fields: [year, quarterOrder], order: [DESC, DESC]}, limit: 1) {
      nodes {
        year
        quarter
      }
    }
  }
`);

const [NewestQuarter, setNewestQuarter] = useState('');
const [spinning, setSpinning] = useState(false);
const [years,setYear] = useState('');
const firstQuarter = 2009;

useEffect(() => {
  if (data.allCourseGradesCsv.nodes.length > 0) {
    const latest = data.allCourseGradesCsv.nodes[0];
    setNewestQuarter(`${latest.quarter} ${latest.year}`);
    const yearDiff = parseInt(latest.year, 10) - firstQuarter;  
    setYear(yearDiff.toString()); 
  }
  spinLogo();
}, []);


const spinLogo = () => {
  setSpinning(true);
  setTimeout(() => {
    setSpinning(false);
  }, animationTime * 1000);
};

  return (
    <Page>
      <h1>Daily Nexus Grades Search</h1>

      <p>
        All UC Santa Barbara students' grades from the past {years} years were made 
        available online on
        {' '}
        <a href="https://github.com/dailynexusdata/grades-data">GitHub</a>
        {' '}
        through Public Records Act requests made by the Daily Nexus. The 
        published data includes the total number of students receiving each 
        grade — before pass/fail and satisfactory/unsatisfactory conversions — 
        for courses offered from Fall 2009 until {NewestQuarter}. Instructors 
        of optionally-graded courses do not know what grading option students 
        have selected, according to the Office of the Registrar.
      </p>
      <p>
        This website allows students to look through grading trends from the
        released data in the three following areas.
        {' '}
      </p>
      <OverviewList>
        <li>
          <b>Courses:</b>
          {' '}
          In this page, you can select an individual instructor and see a 
          timeline of their grading distributions for all of the courses they 
          have taught.
        </li>
        <li>
          <b>GEs:</b>
          {' '}
          This page allows you to find courses that satisfy general education 
          requirements and sorts the courses to show which give the highest 
          grades.
        </li>
        <li>
          <b>Instructors:</b>
          {' '}
          In this page, you can select an individual instructor and see a 
          timeline of their grading distributions for all of the courses they 
          have taught.
        </li>
      </OverviewList>
      <p>
        The grades will be regularly updated on this website as new data 
        arrives.
      </p>
      <p>
        The Nexus has also published four articles about these grades being 
        released, which are linked below.
      </p>
      <p>
  If you would like to give anonymous feedback on the Grades Search website, you may do so through{' '}
  <a href="https://forms.gle/LLy3E4fSvi5E3WabA" target="_blank" rel="noopener noreferrer">
    this form
  </a>.{' '}
  Questions or suggestions can also be directed to{' '}
  <i><a href="mailto:data@dailynexus.com">data@dailynexus.com</a></i>.
</p>
      <h2>View Previous Articles</h2>
       <Article
        href="https://dailynexus.com/2024-09-06/ucsb-awards-majority-a-grades-for-fifth-consecutive-school-year/"
        title="UCSB awards majority A grades for fifth consecutive school year"
        desc="58.82% of course letter grades awarded were A grades, according to data obtained by the Nexus from the UC Santa Barbara Office of the Registrar via a Public Records Act Request. ."
        img={Grades4}
      >
        Sep. 6, 2024 by
        {' '}
        <i>Siddharth Chattoraj</i>
      </Article>
      <Article
        href="https://dailynexus.com/2023-09-01/ucsb-releases-2022-23-course-grades/"
        title=" UCSB releases 2022-23 course grades"
        desc="58.34% of course letter grades awarded were A’s, according to data obtained by the Nexus from the UC Santa Barbara Office of the Registrar via a Public Records Act Request."
        img={Grades3}
      >
        Sep. 1, 2023 by
        {' '}
        <i>Siddharth Chattoraj</i>
      </Article>
      <Article
        href="https://dailynexus.com/2021-10-29/pra-reveals-grading-trends-from-ucsb-over-the-last-six-years/"
        title=" PRA Reveals Grading Trends From UCSB Over the Last Six Years"
        desc="Many courses changed their grading distribution to accommodate students during the pandemic. Compared to grades given before Spring Quarter 2020, 409 courses changed the percentage of A’s, B’s or C’s given out by more than 30 percentage points."
        img={Grades2}
      >
        Oct. 29, 2021 by
        {' '}
        <i>Alex Rudolph</i>
      </Article>
      <Article
        href="https://dailynexus.com/2021-07-09/ucsb-undergrad-releases-all-student-undergraduate-and-graduate-grades-from-past-six-years/"
        title=" UCSB Undergrad Releases All Student Undergraduate and Graduate Grades From Past Six Years"
        desc="Using California’s Freedom of Information Act, a 2021 UCSB alum filed a request with UCSB’s PRA Office, asking for the quarter grades every professor has given over the past six years."
        img={Grades1}
      >
        July 9, 2021 by
        {' '}
        <i>Atmika Iyer</i>
        {' '}
        and
        {' '}
        <i>Alex Rudolph</i>
      </Article>

      <CreditContainer>
        <Credits>
          <a
            href="https://dailynexus.com/category/labyrinth/"
            onMouseEnter={spinLogo}
          >
            <Logo spin={spinning} />
          </a>
          <div>
            <CreditList>
              <b>Created by the Daily Nexus Data Section</b>
              <li>Aaron Zolla</li>
              <li>Alex Rudolph</li>
              <li>Bella Gennuso</li>
              <li>Ian Wen</li>
              <li>John-Michael Kirchner</li>
              <li>Siddharth Chattoraj</li>
              <li>Zach Gold</li>
            </CreditList>
          </div>
        </Credits>
      </CreditContainer>
    </Page>
  );
};
 
export default Index;
